import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Button, FormControlLabel, Grid, IconButton, styled, Switch, } from '@mui/material';
import { LegacyDevice } from 'console/device/device';
import { requestDeviceUpdate } from 'console/redux/devices/items/actions';
import { DevicePermission } from 'console/device/device_ts';
import { UniversalDialog } from 'console/dialogs/universal';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { MaterialDropdown } from 'console/shared/components/fields';
import { DeviceConfig } from 'console/shared/contexts/device/config/types';
import { VehicleDeviceConfig } from 'console/shared/contexts/vehicle-device/config/types';
import { memo, useCallback, useContext, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { createSelector } from '@reduxjs/toolkit';
import { CarModels, CarModelsTranslate } from '../model-view/models/types';
import { selectDevice } from 'entities/device/lib';
import { ColorChangePoint } from './color-change-point';
import * as S from 'console/shared/components/panels/vehicle-panel/components/model-view/styles';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(3),
    },
}));
const options = Object.entries(CarModelsTranslate).map(([model, text]) => ({
    text,
    value: model,
}));
const canChangeModelView = createSelector((state) => state.account, (state, device_id) => selectDevice(state, device_id), (account, dev) => dev != null
    ? LegacyDevice.has_access(account.id, dev, DevicePermission.configure)
    : false);
export const ChangeVehicleModelDialog = memo(function ChangeVehicleModelDialog(props) {
    const { onClose, opened } = props;
    const { device_id } = useContext(DeviceConfig);
    const { car_view } = useContext(VehicleDeviceConfig);
    const onUpdateDevice = useAction(requestDeviceUpdate);
    const [temp_car_view, setTempCarView] = useState({
        ...car_view,
        model: car_view?.model == null ? CarModels.car : car_view.model,
        mirrored: !!car_view?.mirrored,
        color: car_view?.color,
    });
    const can_change = useAppSelector((state) => canChangeModelView(state, device_id));
    const onChangeMirrored = useCallback((e) => {
        const new_val = !e.target.checked;
        setTempCarView({ ...temp_car_view, mirrored: new_val });
    }, [setTempCarView, temp_car_view]);
    const onChangeModel = useCallback((new_value) => {
        setTempCarView({ ...temp_car_view, model: new_value });
    }, [setTempCarView, temp_car_view]);
    const onChangeColor = useCallback((new_value) => {
        setTempCarView({ ...temp_car_view, color: new_value });
    }, [setTempCarView, temp_car_view]);
    const onAccept = useCallback(() => {
        onUpdateDevice(device_id, {
            car_view: temp_car_view,
        }, {
            ignore_server_response: true,
            obey_only_last_response: true,
        });
        onClose();
    }, [onClose, device_id, temp_car_view]);
    const handleForwardStep = useCallback(() => {
        const index = options.findIndex((item) => item.value === temp_car_view.model);
        setTempCarView({
            ...temp_car_view,
            model: options[index !== options.length - 1 ? index + 1 : 0].value,
        });
    }, [options, temp_car_view, setTempCarView]);
    const handleBackStep = useCallback(() => {
        const index = options.findIndex((item) => item.value === temp_car_view.model);
        setTempCarView({
            ...temp_car_view,
            model: options[index !== 0 ? index - 1 : options.length - 1].value,
        });
    }, [options, temp_car_view, setTempCarView]);
    const onSwitching = useCallback((index, type) => {
        let new_index = Math.round(index);
        if (type === 'end') {
            if (new_index === options.length - 1) {
                new_index = 0;
            }
            else {
                new_index = options.length - 1;
            }
            onChangeTabIndex(new_index);
        }
    }, [temp_car_view]);
    const onChangeTabIndex = useCallback((index) => {
        setTempCarView({
            ...temp_car_view,
            model: options[index].value,
        });
    }, [setTempCarView, temp_car_view]);
    return (_jsx(UniversalDialog, { open: opened, onClose: onClose, maxWidth: "sm", title: "\u0418\u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u0435 \u0430\u0432\u0442\u043E", footer: _jsxs(Box, { sx: { mb: 1 }, children: [_jsx(Button, { onClick: onClose, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { onClick: onAccept, color: "primary", disabled: !can_change, children: "\u041F\u0440\u0438\u043D\u044F\u0442\u044C" })] }), children: _jsxs(Grid, { container: true, spacing: 2, justifyContent: "center", alignItems: "center", children: [_jsxs(Grid, { item: true, container: true, xs: 12, style: { flexWrap: 'nowrap', alignItems: 'stretch' }, children: [_jsx(Grid, { item: true, style: { flexGrow: 0, flexShrink: 0 }, children: _jsx(IconButton, { sx: {
                                    height: '100%',
                                    '&:hover': {
                                        background: 'none',
                                    },
                                }, "aria-label": "arrow back", onClick: handleBackStep, size: "large", children: _jsx(KeyboardArrowLeft, {}) }) }), _jsx(Grid, { item: true, style: { flexGrow: 1, overflow: 'hidden' }, children: _jsx(SwipeableViews, { index: options.findIndex((item) => item.value === temp_car_view.model), onChangeIndex: onChangeTabIndex, onSwitching: onSwitching, className: "car-style-dialog", children: options.map((item) => {
                                    return (_jsx(Box, { position: "relative", width: "100%", height: "100%", display: "flex", p: 1, justifyContent: "center", children: _jsx(S.ModelView, { center: true, children: _jsxs(Box, { position: "relative", children: [_jsx(ColorChangePoint, { color: temp_car_view.color, model: item.value, is_mirrored: temp_car_view.mirrored, onChangeColor: onChangeColor }), _jsx(S.CarModel, { model: item.value, car_color: temp_car_view.color, mirror: temp_car_view.mirrored, online: true })] }) }) }, item.value));
                                }) }) }), _jsx(Grid, { item: true, style: { flexGrow: 0, flexShrink: 0 }, children: _jsx(IconButton, { sx: {
                                    height: '100%',
                                    '&:hover': {
                                        background: 'none',
                                    },
                                }, "aria-label": "arrow forward", onClick: handleForwardStep, size: "large", children: _jsx(KeyboardArrowRight, {}) }) })] }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsx(ControlWithoutValidation, { label: "\u0412\u0438\u0434 \u0442\u0435\u0445\u043D\u0438\u043A\u0438", onChange: onChangeModel, value: temp_car_view.model, field: (input_props) => (_jsx(MaterialDropdown, { ...input_props, options: options })) }) }), _jsx(Grid, { item: true, xs: 12, sm: 6, children: _jsx(StyledFormControlLabel, { label: temp_car_view.mirrored ? 'Влево' : 'Вправо', control: _jsx(Switch, { checked: !temp_car_view.mirrored, onChange: onChangeMirrored, color: "primary" }) }) })] }) }));
});
